import { useEffect, useLayoutEffect } from 'react'
import { ToastProvider } from '@panwds/react-ui'
import { useStoreReducer, stateRef, authStateRef } from '../activation/store'
import reducer from '../activation/reducer'
import { LG_BASE_FONT_SIZE, TSG_NONE } from '../activation/constants'
import ZTPDeviceActivationForm from './ZTPDeviceActivationForm'
import HeroBanner from './HeroBanner'
import Header from './Header'

import type { VFC } from 'react'
import { SupportAccount, AuthState, indexTsgInstances, actions } from '@sparky/framework'
import type { CommonStoreState, IndexedSupportAccounts, StateRef } from '../activation/store'
import PermissionError from './PermissionError'
import { ScreenFooter } from '../activation/components'
import { updateStateProps } from '../activation/actions'


const getIndexedSupportAccounts = (supportAccountIds?: SupportAccount[]) => {
  const accounts = Array.from(supportAccountIds || []) // clone
  const accountMap = new Map<string | number, SupportAccount>()
  for (const acc of accounts) {
    accountMap.set(+acc.accountid, acc)
    accountMap.set(String(acc.accountid), acc)
  }
  Object.defineProperties(accounts, {
    get: {
      value: accountMap.get.bind(accountMap)
    },
    isLoaded: {
      value: Array.isArray(supportAccountIds)
    },
    supportAccountIds: { value: supportAccountIds },
  })
  return accounts as IndexedSupportAccounts
}
const DEFAULT_EMPTY_INSTANCES = indexTsgInstances([], '')
const getUpdatedState = (auth: AuthState, stateRef?: StateRef): CommonStoreState => {
  const { tsg_id, apps, user, tsgs, instances: globalInstances } = auth
  const selectedTsgId = stateRef?.current.tsg?.id
  const tsg = (selectedTsgId === TSG_NONE.id && TSG_NONE) ||
    (selectedTsgId && tsgs.get(selectedTsgId)) || undefined
  const currentSupportAccounts = stateRef?.current.supportAccounts
  const userSupportAccount = user?.supportAccountIds
  const supportAccounts = userSupportAccount === (currentSupportAccounts as any)?.supportAccountIds ? currentSupportAccounts : getIndexedSupportAccounts(user?.supportAccountIds)
  const loadingTsg = Boolean(tsg && tsg_id !== selectedTsgId)
  const instances = !selectedTsgId ? DEFAULT_EMPTY_INSTANCES : tsg_id === selectedTsgId ? globalInstances : (
    stateRef?.current.instances || indexTsgInstances([], selectedTsgId)
  )
  return {
    globalTsgId: tsg_id,
    tsg,
    tsgs,
    apps,
    supportAccounts,
    instances,
    loadingTsg,
    loading: !(apps?.length && (supportAccounts as any)?.isLoaded),
  } as CommonStoreState
}

const DeviceActivationContainer: VFC<{ auth: AuthState, location: { pathname: string } }> = ({ auth, location }) => {
  const { access, user } = auth
  const isUserHasPermission = (access?.[0].roleNames?.find(role => ['superuser', 'business_admin', 'msp_superuser'].indexOf(role) > -1))
  const state = useStoreReducer(reducer, {}, () => {
    return getUpdatedState(auth)
  })
  useEffect(() => {
    const appClassList = document.querySelector('.sparky-app')?.classList
    appClassList?.add('sparky-app-bar-z-0')
    return () => {
      appClassList?.remove('sparky-app-bar-z-0')
    }
  }, [])
  useLayoutEffect(() => { // TODO: hacks
    const htmlStyle = document.documentElement.style
    const bodyFontSize = htmlStyle.fontSize
    if (bodyFontSize === LG_BASE_FONT_SIZE) {
      return
    }
    htmlStyle.fontSize = LG_BASE_FONT_SIZE
    return () => {
      htmlStyle.fontSize = bodyFontSize
    }
  }, [])
  useEffect(() => {
    const newState = getUpdatedState(auth, stateRef)
    if (!newState.loading) {
      updateStateProps(newState)
    }
  }, [auth])

  const selectedTsgId = state.tsg?.id
  useEffect(() => {
    if (!selectedTsgId) {
      return updateStateProps({
        loadingTsg: false,
        instances: DEFAULT_EMPTY_INSTANCES,
      })
    }
    const sameTsgAsGlobal = selectedTsgId === state.globalTsgId
    updateStateProps({
      loadingTsg: !sameTsgAsGlobal,
      instances: sameTsgAsGlobal ?
        authStateRef.current!.instances :
        indexTsgInstances([], selectedTsgId || ''),
    }) // even for same as globalTsgId, still load lastest
    actions.loadTsgInstances(selectedTsgId).then((instances) => {
      if (stateRef.current.tsg?.id === instances.tsg_id) {
        updateStateProps({
          loadingTsg: false,
          instances,
        })
      }
    }, (err) => {
      if (stateRef.current.tsg?.id === selectedTsgId) {
        updateStateProps({
          loadingTsg: false,
          instances: indexTsgInstances.fromError(err, selectedTsgId),
        }) // TODO: error msg
      }
    })
  }, [selectedTsgId, state.globalTsgId])

  return (
    <div data-testid='sparky-activation-page' className='sparky-activation-page cm-overflow-y-auto cm-fixed cm-top-0 cm-left-0 cm-right-0 cm-bottom-0 tw-bg-white dark:cm-bg-gray-1050'>
      <Header title='ZTP NGFW Activation' subTitle='Activate your ZTP next-genration firewall.' linkText='Learn More' linkUrl='https://www.paloaltonetworks.com/'></Header>
      <div className='tw-flex tw-justify-center  tw-min-h-screen tw-bg-gray-200'>
        <div className='tw-text-grey-darker tw-text-center tw-bg-grey-light tw-px-4 tw-py-2 tw-m-2 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-solid cm-border-[#DADBDB] tw-space-y-4'>
          {!isUserHasPermission ? <PermissionError heading='You do not have an account with Installer credentials to access this page. Please request permission from your system admin if you think this is an error.' url='https://docs.paloaltonetworks.com/common-services/identity-and-access-access-management/manage-identity-and-access/about-roles-and-permissions#:~:text=Business%20Administrator' /> : (<>
            <HeroBanner heading='Welcome to ZTP Activation'></HeroBanner>
            <ToastProvider>
              <ZTPDeviceActivationForm state={state} notonorafter={user?.notonorafter}></ZTPDeviceActivationForm>
              <ScreenFooter />
            </ToastProvider></>)}
        </div>
      </div>
    </div>
  )
}
export default DeviceActivationContainer
